"use client"

import { useEffect, useRef } from "react"

/** Migration only: never registers a worker or delays analytics loading. */
export default function RetireAnalyticsServiceWorker() {
  const started = useRef(false)

  useEffect(() => {
    // React Strict Mode can replay effects; do not race two worker updates.
    if (started.current) return
    started.current = true

    async function retireLegacyWorker() {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.allSettled(
          registrations.map(async (registration) => {
            const isLegacyWorker = [
              registration.active,
              registration.waiting,
              registration.installing,
            ].some((worker) => {
              if (!worker) return false
              const url = new URL(worker.scriptURL)
              return url.origin === location.origin && url.pathname === "/sw.js"
            })

            if (!isLegacyWorker) return

            try {
              // Replace the fetch handler in already controlled tabs with the
              // retiring worker before removing its registration.
              await registration.update()
            } finally {
              await registration.unregister()
            }
          }),
        )
      }
    }

    async function clearLegacyCaches() {
      if (!("caches" in window)) return
      const names = await caches.keys()
      await Promise.all(
        names
          .filter((name) => name.startsWith("analytics-cache-"))
          .map((name) => caches.delete(name)),
      )
    }

    // Cache cleanup also runs if registration access is unavailable or fails.
    void Promise.allSettled([retireLegacyWorker(), clearLegacyCaches()])
  }, [])

  return null
}
